import { useContext, useState } from "react";
import { InviteGrid } from "../styles.css";
import { Header, Section } from "../../../components/layout";
import { PolicyInviteForm } from "./PolicyInviteForm";
import InvitationService, {
  EmailCcPrefill,
  Invitation,
  InvitationPrefill,
} from "../../../services/invitation/invitation.service";
import { useHistory, useLocation } from "react-router-dom";
import { PolicyHolderScInvite } from "./PolicyHolderScInvite.fields";
import { useAsyncEffect } from "../../../hooks/useAsyncEffect";
import { Spinner } from "@safetyculture/sc-web-ui";
import { hasAllowedRoles } from "../../../helpers";
import { AuthContext } from "../../../contexts";
import styled from "styled-components";
import { Spacing } from "../../../components/layout/Spacing";
import { usePolicyQuery } from "../../../hooks/queries/usePolicyQuery";
import { SelectOption } from "../../../components/form/SelectWithValidation/SelectWithValidation";
import { Role } from "../../../types/user.type";
import { brand } from "../../../config/brand";
import { useQueryClient } from "react-query";

type Props = {};

export const InvitePolicyHolderToSC: React.FC<Props> = () => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const history = useHistory();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const policy_id = query.get("id");
  const policy_query = usePolicyQuery(policy_id);
  const [invite_data_prefill, setInviteDataPrefill] =
    useState<InvitationPrefill | undefined | null>(undefined);
  const is_privileged_user = hasAllowedRoles(user?.roles, {
    allowed: [Role.Admin, Role.SuperAdmin],
  });

  useAsyncEffect(async () => {
    if (policy_id) {
      try {
        const invite_prefill = await InvitationService.fetchPrefillForPolicy(
          policy_id,
        );
        setInviteDataPrefill(invite_prefill);
      } catch {
        setInviteDataPrefill(null);
      }
    }
  }, [policy_id]);

  const handleSubmit = async (fields: PolicyHolderScInvite) => {
    const invite: Omit<Invitation, "status"> = {
      firstname: fields.firstname,
      lastname: fields.lastname,
      email: fields.email,
      policy_number: invite_data_prefill?.fields.policy_number,
      customer_org_name: invite_data_prefill?.fields.customer_org_name,
      role: Role.Customer, // Policy holders are always customer type
      data: {
        scheme: fields.toolkit_code?.meta?.code || fields.toolkit_code?.label, // this API expects toolkit's code instead of id + default to label if no meta as label is always gonna be toolkit code whereas value is meant to be toolkit id
      },
      sender_org_id: invite_data_prefill?.fields.sender_org_id,
      sender_first_name: fields.sender_first_name,
      sender_last_name: fields.sender_last_name,
      email_cc:
        fields.email_cc?.map((o: SelectOption<EmailCcPrefill>) => o.value) ||
        [],
      email_bcc:
        fields.email_bcc?.map((o: SelectOption<EmailCcPrefill>) => o.value) ||
        [],
    };
    const created = await InvitationService.createInvitation(invite);

    if (created) {
      queryClient.invalidateQueries(["use-single-policy-query", policy_id]);
      history.replace(`/policies/${policy_id}`); // disable navigating back to form via browser back button
    }
  };

  return (
    <InviteGrid>
      <Header
        title={`Invite ${
          policy_query?.data?.policy_data?.BUSINESS_NAME ||
          policy_query?.data?.policy_data?.business_name ||
          ""
        } to ${brand.parentName}`}
        titleIcon="mail"
        titleIconPosition="end"
        type="article"
        withLink={{ path: "/policies", text: "All Policies" }}
      />
      <Section>
        {invite_data_prefill === undefined && (
          <>
            <Spacing $height={200} />
            <Spinner />
          </>
        )}
        {invite_data_prefill !== undefined && (
          <VerticallySpaced>
            <PolicyInviteForm
              is_policy_invitate_to_sc_disabled={
                policy_query.data?.do_not_invite || false
              }
              onSubmit={handleSubmit}
              invite_prefill={invite_data_prefill}
              policy_id={policy_id}
              is_privileged_user={is_privileged_user}
            />
          </VerticallySpaced>
        )}
      </Section>
    </InviteGrid>
  );
};

const VerticallySpaced = styled.div`
  margin-top: 32px;
`;
